import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, ArchiveRestore, Calendar, Dumbbell, Layers, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useGymLogo } from '../../../hooks/useGymLogo';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import LoadingSpinner from '../../common/LoadingSpinner';
import ConfirmModal from '../../ui/ConfirmModal';
import PDFPreviewModal from '../pdf/PDFPreviewModal';
import { ReactComponent as WorkoutBtnTemplateIcon } from '../../../assets/icons/workout-btn-template.svg';
import { ReactComponent as WorkoutActionViewIcon } from '../../../assets/icons/workout-action-view.svg';
import { ReactComponent as WorkoutActionEditIcon } from '../../../assets/icons/workout-action-edit.svg';
import { ReactComponent as WorkoutActionPrintIcon } from '../../../assets/icons/workout-action-print.svg';
import { ReactComponent as WorkoutActionDeleteIcon } from '../../../assets/icons/workout-action-delete.svg';

function WorkoutActionIconButton({ label, disabledReason, onClick, disabled, Icon, variant = 'default' }) {
    const hoverText = disabled && disabledReason ? disabledReason : label;
    const variantClass =
        variant === 'destructive'
            ? 'text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40'
            : variant === 'template'
              ? 'text-amber-700 hover:bg-amber-50 dark:text-amber-200 dark:hover:bg-amber-950/40'
              : variant === 'archive'
                ? 'text-orange-700 hover:bg-orange-50 dark:text-orange-300 dark:hover:bg-orange-950/40'
              : 'text-gray-600 hover:bg-gray-100 dark:text-white/80 dark:hover:bg-white/10';

    return (
        <div className="relative inline-flex group">
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={hoverText}
                title={hoverText}
                className={`flex min-w-[2.75rem] flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1 transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${variantClass}`}
            >
                <Icon className="h-4 w-4 shrink-0" aria-hidden />
                <span className="max-w-[4rem] text-center text-[9px] font-medium leading-tight line-clamp-2">{label}</span>
            </button>
        </div>
    );
}

export default function MemberWorkoutsTab({
    member,
    workoutsRefreshKey,
    onShowCreateModal,
    onShowAssignTemplateModal,
    onShowEditModal,
    onShowWorkoutDetail,
    onConvertToTemplate,
}) {
    const { getAuthenticatedApi, currentUser } = useAuth();
    const { t, i18n } = useTranslation();
    const { logoUrl } = useGymLogo();
    const isMobile = useMediaQuery('(max-width: 1023px)');
    const [workouts, setWorkouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [workoutView, setWorkoutView] = useState('active');
    const [showPDFModal, setShowPDFModal] = useState(false);
    const [selectedWorkoutForPDF, setSelectedWorkoutForPDF] = useState(null);
    const [workoutToDelete, setWorkoutToDelete] = useState(null);
    const [deletingWorkout, setDeletingWorkout] = useState(false);
    const [workoutToArchive, setWorkoutToArchive] = useState(null);
    const [archivingWorkout, setArchivingWorkout] = useState(false);
    const [restoringWorkoutId, setRestoringWorkoutId] = useState(null);

    const fetchWorkouts = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) {
                setLoading(true);
            }
            const api = getAuthenticatedApi();
            const userAssignmentsResponse = await api.get(`/user_assignments.php?user_id=${member.id}`);
            const assignments = userAssignmentsResponse.data || [];

            const workoutPlansPromises = assignments.map(async (assignment) => {
                const isActive = assignment.active === 1 || assignment.active === true || assignment.active === '1';
                try {
                    const schedaResponse = await api.get(`/schede.php?id=${assignment.scheda_id}`);
                    const schedaData = schedaResponse.data;
                    return {
                        id: assignment.scheda_id,
                        name: assignment.scheda_nome || `Scheda ${assignment.scheda_id}`,
                        description: assignment.scheda_descrizione || '',
                        total_exercises: schedaData.esercizi ? schedaData.esercizi.length : 0,
                        assigned_at: assignment.assigned_date,
                        created_at: assignment.data_creazione,
                        workout_in_progress: !!assignment.workout_in_progress,
                        is_active: isActive,
                    };
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Errore caricamento dettagli scheda:', assignment.scheda_id, err);
                    return {
                        id: assignment.scheda_id,
                        name: assignment.scheda_nome || `Scheda ${assignment.scheda_id}`,
                        description: assignment.scheda_descrizione || '',
                        total_exercises: 0,
                        assigned_at: assignment.assigned_date,
                        created_at: assignment.data_creazione,
                        workout_in_progress: !!assignment.workout_in_progress,
                        is_active: isActive,
                    };
                }
            });

            const workoutPlans = await Promise.all(workoutPlansPromises);
            setWorkouts(workoutPlans);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Errore caricamento schede:', err);
            if (!silent) {
                setWorkouts([]);
            }
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [getAuthenticatedApi, member.id]);

    useEffect(() => {
        fetchWorkouts();
    }, [fetchWorkouts]);

    useEffect(() => {
        if (workoutsRefreshKey === 0) return;
        fetchWorkouts({ silent: true });
    }, [workoutsRefreshKey, fetchWorkouts]);

    const memberId = member?.id ?? null;
    useEffect(() => {
        if (memberId == null) return;
        const interval = setInterval(() => { fetchWorkouts({ silent: true }); }, 60 * 1000);
        return () => clearInterval(interval);
    }, [memberId, fetchWorkouts]);

    const activeWorkouts = useMemo(
        () => workouts.filter((workout) => workout.is_active),
        [workouts],
    );
    const archivedWorkouts = useMemo(
        () => workouts.filter((workout) => !workout.is_active),
        [workouts],
    );
    const displayedWorkouts = workoutView === 'archived' ? archivedWorkouts : activeWorkouts;

    const handleViewWorkout = async (workout) => {
        try {
            const api = getAuthenticatedApi();
            const response = await api.get(`/schede.php?id=${workout.id}`);
            onShowWorkoutDetail(response.data);
        } catch (err) {
            console.error('Errore caricamento dettagli scheda:', err);
            toast.error(t('gym.membersManager.workouts.loadError'));
        }
    };

    const handleEditWorkout = async (workout) => {
        try {
            const api = getAuthenticatedApi();
            const response = await api.get(`/schede.php?id=${workout.id}`);
            onShowEditModal(response.data);
        } catch (err) {
            console.error('Errore caricamento scheda per modifica:', err);
            toast.error(t('gym.membersManager.workouts.loadError'));
        }
    };

    const handlePrintWorkout = async (workout) => {
        try {
            const api = getAuthenticatedApi();
            const response = await api.get(`/schede.php?id=${workout.id}`);
            setSelectedWorkoutForPDF({
                ...response.data,
                // schede.php non espone la data assegnazione: la prendiamo dalla lista
                assigned_at: workout.assigned_at || response.data?.assigned_at || null,
            });
            setShowPDFModal(true);
        } catch (err) {
            console.error('Errore caricamento scheda per stampa:', err);
            toast.error(t('gym.membersManager.workouts.loadError'));
        }
    };

    const handleConfirmDeleteWorkout = async () => {
        if (!workoutToDelete) return;
        try {
            setDeletingWorkout(true);
            const api = getAuthenticatedApi();
            const response = await api.delete(`/schede.php?id=${workoutToDelete.id}`);
            toast.success(response.data?.message || t('gym.membersManager.workouts.deleteSuccess'));
            setWorkoutToDelete(null);
            await fetchWorkouts({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.workouts.deleteError'));
        } finally {
            setDeletingWorkout(false);
        }
    };

    const handleConfirmArchiveWorkout = async () => {
        if (!workoutToArchive) return;
        try {
            setArchivingWorkout(true);
            const api = getAuthenticatedApi();
            const response = await api.patch(`/schede.php?id=${workoutToArchive.id}`, { active: 0 });
            toast.success(response.data?.message || t('gym.membersManager.workouts.archiveSuccess'));
            setWorkoutToArchive(null);
            await fetchWorkouts({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.workouts.archiveError'));
        } finally {
            setArchivingWorkout(false);
        }
    };

    const handleRestoreWorkout = async (workout) => {
        try {
            setRestoringWorkoutId(workout.id);
            const api = getAuthenticatedApi();
            const response = await api.patch(`/schede.php?id=${workout.id}`, { active: 1 });
            toast.success(response.data?.message || t('gym.membersManager.workouts.restoreSuccess'));
            await fetchWorkouts({ silent: true });
            setWorkoutView('active');
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.workouts.restoreError'));
        } finally {
            setRestoringWorkoutId(null);
        }
    };

    const dateLocale = i18n.language?.startsWith('it') ? 'it-IT' : undefined;

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="space-y-3 pb-2">
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-gray-50/80 px-2 py-2 dark:border-white/10 dark:bg-gray-900/50">
                <div className="flex flex-wrap items-center gap-1.5">
                    <button
                        type="button"
                        onClick={() => setWorkoutView('active')}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                            workoutView === 'active'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-white/80 dark:hover:bg-gray-700'
                        }`}
                    >
                        {t('gym.membersManager.workouts.statusTabs.active')}
                        <span className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold ${
                            workoutView === 'active' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-white'
                        }`}
                        >
                            {activeWorkouts.length}
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setWorkoutView('archived')}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                            workoutView === 'archived'
                                ? 'bg-orange-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-white/80 dark:hover:bg-gray-700'
                        }`}
                    >
                        <Archive size={13} aria-hidden />
                        {t('gym.membersManager.workouts.statusTabs.archived')}
                        <span className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold ${
                            workoutView === 'archived' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-white'
                        }`}
                        >
                            {archivedWorkouts.length}
                        </span>
                    </button>
                </div>
                {workoutView === 'active' && (
                    <div className="ml-auto flex flex-wrap items-center gap-1.5">
                        <button
                            type="button"
                            onClick={(e) => onShowAssignTemplateModal(e)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 dark:border-indigo-500/30 dark:bg-gray-800 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
                        >
                            <Layers size={14} aria-hidden />
                            {t('gym.membersManager.workouts.fromTemplate')}
                        </button>
                        <button
                            type="button"
                            onClick={(e) => onShowCreateModal(e)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                        >
                            <Plus size={14} aria-hidden />
                            {t('gym.membersManager.workouts.newWorkout')}
                        </button>
                    </div>
                )}
            </div>

            {displayedWorkouts.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/40">
                    <ul className="divide-y divide-gray-100 dark:divide-white/5">
                        {displayedWorkouts.map((workout) => {
                            const assignedLabel = new Date(workout.assigned_at || workout.created_at).toLocaleDateString(dateLocale, {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                            });
                            const isArchivedView = workoutView === 'archived';
                            return (
                                <li key={workout.id} className="px-2 py-2 sm:px-3">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                                            isArchivedView
                                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300'
                                                : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
                                        }`}
                                        >
                                            {isArchivedView ? <Archive size={16} aria-hidden /> : <Dumbbell size={16} aria-hidden />}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                                                    {workout.name}
                                                </p>
                                                {workout.workout_in_progress && (
                                                    <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                                                        {t('gym.membersManager.workouts.inProgressBadge')}
                                                    </span>
                                                )}
                                                {isArchivedView && (
                                                    <span className="shrink-0 rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-900 dark:bg-orange-900/40 dark:text-orange-100">
                                                        {t('gym.membersManager.workouts.archivedBadge')}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-white/55">
                                                <span>{t('gym.membersManager.workouts.exercisesCount', { count: workout.total_exercises })}</span>
                                                <span className="inline-flex items-center gap-1">
                                                    <Calendar size={11} aria-hidden />
                                                    {assignedLabel}
                                                </span>
                                            </p>
                                            {workout.description && !isMobile && (
                                                <p className="mt-0.5 line-clamp-1 text-xs text-gray-400 dark:text-white/45">
                                                    {workout.description}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex shrink-0 flex-wrap items-end justify-end gap-1">
                                            <WorkoutActionIconButton
                                                label={t('gym.membersManager.workouts.actionView')}
                                                onClick={() => handleViewWorkout(workout)}
                                                Icon={WorkoutActionViewIcon}
                                            />
                                            {!isArchivedView && (
                                                <>
                                                    <WorkoutActionIconButton
                                                        label={t('gym.membersManager.workouts.actionEdit')}
                                                        disabledReason={t('gym.membersManager.workouts.actionEditDisabled')}
                                                        onClick={() => !workout.workout_in_progress && handleEditWorkout(workout)}
                                                        disabled={workout.workout_in_progress}
                                                        Icon={WorkoutActionEditIcon}
                                                    />
                                                    <WorkoutActionIconButton
                                                        label={t('gym.membersManager.workouts.actionSaveAsTemplate')}
                                                        onClick={() => onConvertToTemplate(workout)}
                                                        Icon={WorkoutBtnTemplateIcon}
                                                        variant="template"
                                                    />
                                                </>
                                            )}
                                            <WorkoutActionIconButton
                                                label={t('gym.membersManager.workouts.actionPrint')}
                                                onClick={() => handlePrintWorkout(workout)}
                                                Icon={WorkoutActionPrintIcon}
                                            />
                                            {isArchivedView ? (
                                                <WorkoutActionIconButton
                                                    label={t('gym.membersManager.workouts.actionRestore')}
                                                    onClick={() => handleRestoreWorkout(workout)}
                                                    disabled={restoringWorkoutId === workout.id}
                                                    Icon={ArchiveRestore}
                                                    variant="archive"
                                                />
                                            ) : (
                                                <>
                                                    <WorkoutActionIconButton
                                                        label={t('gym.membersManager.workouts.actionArchive')}
                                                        disabledReason={t('gym.membersManager.workouts.actionArchiveDisabled')}
                                                        onClick={() => !workout.workout_in_progress && setWorkoutToArchive(workout)}
                                                        disabled={workout.workout_in_progress}
                                                        Icon={Archive}
                                                        variant="archive"
                                                    />
                                                    <WorkoutActionIconButton
                                                        label={t('gym.membersManager.workouts.actionDelete')}
                                                        disabledReason={t('gym.membersManager.workouts.actionDeleteDisabled')}
                                                        onClick={() => !workout.workout_in_progress && setWorkoutToDelete(workout)}
                                                        disabled={workout.workout_in_progress}
                                                        Icon={WorkoutActionDeleteIcon}
                                                        variant="destructive"
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-4 py-10 text-center dark:border-white/10 dark:bg-gray-900/30">
                    {workoutView === 'archived' ? (
                        <Archive size={40} className="mx-auto mb-2 text-gray-300 dark:text-white/30" aria-hidden />
                    ) : (
                        <Dumbbell size={40} className="mx-auto mb-2 text-gray-300 dark:text-white/30" aria-hidden />
                    )}
                    <p className="text-sm font-medium text-gray-700 dark:text-white/85">
                        {workoutView === 'archived'
                            ? t('gym.membersManager.workouts.noArchivedWorkouts')
                            : t('gym.membersManager.workouts.noWorkouts')}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-white/55">
                        {workoutView === 'archived'
                            ? t('gym.membersManager.workouts.noArchivedWorkoutsDescription')
                            : t('gym.membersManager.workouts.createFirst')}
                    </p>
                </div>
            )}

            {showPDFModal && selectedWorkoutForPDF && (
                <PDFPreviewModal
                    isOpen={showPDFModal}
                    onClose={() => {
                        setShowPDFModal(false);
                        setSelectedWorkoutForPDF(null);
                    }}
                    workout={selectedWorkoutForPDF}
                    athlete={member}
                    gymLogo={logoUrl}
                    gymName={currentUser?.gym_name || 'FitGym'}
                />
            )}

            <ConfirmModal
                isOpen={Boolean(workoutToDelete)}
                onClose={() => {
                    if (deletingWorkout) return;
                    setWorkoutToDelete(null);
                }}
                onConfirm={handleConfirmDeleteWorkout}
                title={t('gym.membersManager.workouts.deleteConfirmTitle')}
                message={workoutToDelete ? t('gym.membersManager.workouts.deleteConfirmMessage', { name: workoutToDelete.name }) : ''}
                confirmText={t('gym.membersManager.workouts.actionDelete')}
                cancelText={t('gym.editExtendedProfile.actions.cancel')}
                variant="danger"
                loading={deletingWorkout}
            />

            <ConfirmModal
                isOpen={Boolean(workoutToArchive)}
                onClose={() => {
                    if (archivingWorkout) return;
                    setWorkoutToArchive(null);
                }}
                onConfirm={handleConfirmArchiveWorkout}
                title={t('gym.membersManager.workouts.archiveConfirmTitle')}
                message={workoutToArchive ? t('gym.membersManager.workouts.archiveConfirmMessage', { name: workoutToArchive.name }) : ''}
                confirmText={t('gym.membersManager.workouts.actionArchive')}
                cancelText={t('gym.editExtendedProfile.actions.cancel')}
                variant="warning"
                loading={archivingWorkout}
            />
        </div>
    );
}
